import { addDays, format, isSameDay, isValid, parse, startOfDay, subDays } from 'date-fns';

export const NDC_REGEX = /^\d{10,11}$|^(\d{4,5}-\d{3,4}-\d{1,2})$/;

export const isValidNdc = (ndc) => NDC_REGEX.test(String(ndc).trim());

export const lineFieldError = (showErrors, value) => showErrors && !String(value ?? '').trim();

// Lot numbers are capped well under the varchar(100) storage limit — real pharma lot/batch numbers
// are short. The cap is configurable via env, and falls back to a default of 36 when the var is
// missing/empty/non-numeric/<=0 (the value arrives as a build-time string, so it must be parsed
// and guarded).
const configuredLotMax = Number.parseInt(process.env.REACT_APP_LOT_NUMBER_MAX_LENGTH, 10);
export const LOT_NUMBER_MAX_LENGTH =
  Number.isFinite(configuredLotMax) && configuredLotMax > 0 ? configuredLotMax : 36;

// Vendor lot formats use punctuation freely, so every printable ASCII character is accepted except
// the unsafe ones below. Kept out deliberately:
//   - whitespace and control characters (0x00-0x20, 0x7F) — invisible/trailing characters make two
//     lots that look identical fail to match on lookup and scan
//   - non-ASCII (0x80+) — Unicode homoglyphs and bidi marks are unresolvable ambiguity in a value
//     that is scanned, compared and printed on labels
//   - < > & " ' ` \ ; | $ — the characters that carry meaning when a value is interpolated into
//     markup, a quoted string or a shell command; no real lot number contains them
// UNSAFE_LOT_CHARACTERS is the display copy of the regex's exclusion set — keep the two in sync.
export const UNSAFE_LOT_CHARACTERS = '< > & " \' ` \\ ; | $';
export const LOT_NUMBER_REGEX = /^(?!.*[<>&"'`\\;|$])[\x21-\x7E]+$/;

export const isValidLotNumber = (lot) => {
  const trimmed = String(lot ?? '').trim();
  return (
    trimmed.length > 0 && trimmed.length <= LOT_NUMBER_MAX_LENGTH && LOT_NUMBER_REGEX.test(trimmed)
  );
};

export const lotFieldError = (showErrors, lot) => {
  const trimmed = String(lot ?? '').trim();
  // Empty is only flagged after a save attempt; anything typed that's invalid (bad characters or
  // over the max length) flags immediately, so a long paste isn't silently accepted with no signal.
  if (!trimmed) return showErrors;
  return !isValidLotNumber(lot);
};

export const getLotHelperText = (showErrors, lot) => {
  const trimmed = String(lot ?? '').trim();
  if (!trimmed) return showErrors ? 'Required' : '';
  if (!LOT_NUMBER_REGEX.test(trimmed)) return `No spaces or ${UNSAFE_LOT_CHARACTERS}`;
  if (trimmed.length > LOT_NUMBER_MAX_LENGTH) return `Max ${LOT_NUMBER_MAX_LENGTH} characters`;
  return '';
};

export const formatUnverifiedQty = (qty) => {
  const n = Number(qty);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const normalizeNdc = (ndc) =>
  String(ndc ?? '')
    .replace(/-/g, '')
    .trim();

/**
 * Names the drug a lot number turns out to belong to, for the message that tells an operator
 * which of the two things in front of them is wrong.
 *
 * Lives here beside getLotNdcMismatch rather than in a dialog, because three separate entry
 * points now report this and a formatter that drifts between them would describe the same lot
 * differently depending on where it was found.
 */
export const describeLotDrug = (entry) => {
  const name = `${entry.drugName ?? ''} ${entry.strength ?? ''}`.trim();
  return name ? `${name} (NDC ${entry.ndc})` : `NDC ${entry.ndc}`;
};

/**
 * A quantity in the tally preview line, where null means "not answerable yet" — no quantity
 * typed, or a tally over the cap — which is not the same statement as zero.
 */
export const formatPreviewQty = (value) => (value === null ? '—' : formatUnverifiedQty(value));

/**
 * The looked-up entries that belong to THIS drug.
 *
 * The lookup is by lot number alone and deliberately cross-NDC - that is what surfaces a lot
 * recorded against the wrong drug - so anything read off an entry as a fact about "this lot" has
 * to be narrowed to the NDC in hand first. Otherwise a foreign row supplies the receipt's BUD.
 *
 * Digits only, like getLotNdcMismatch: the picker and the lot row can punctuate the same NDC
 * differently, and a hyphen is not a different drug.
 */
export const getLotEntriesForNdc = ({ entries, ndc }) => {
  const selected = normalizeNdc(ndc);
  if (!selected) return [];
  return (entries ?? []).filter((entry) => normalizeNdc(entry?.ndc) === selected);
};

export const getLotNdcMismatch = ({ entries, ndc }) => {
  const selected = normalizeNdc(ndc);
  if (!selected) return [];
  const seen = new Set();
  return (entries ?? []).filter((entry) => {
    const entryNdc = normalizeNdc(entry?.ndc);
    if (!entryNdc || entryNdc === selected || seen.has(entryNdc)) return false;
    seen.add(entryNdc);
    return true;
  });
};

export const getTallyState = ({ unverifiedQty, quantityReceived, tallyQty }) => {
  const unverified = Math.max(0, Number(unverifiedQty) || 0);
  const receivedText = String(quantityReceived ?? '').trim();
  const received = receivedText === '' ? null : Number(receivedText);
  const hasReceived = received !== null && !Number.isNaN(received);
  const max = hasReceived ? Math.min(unverified, received) : unverified;
  const suggested = max;
  const tallyText = String(tallyQty ?? '').trim();
  const touched = tallyText !== '';
  const parsed = touched ? Number(tallyText) : suggested;
  let error = '';
  if (touched && (Number.isNaN(parsed) || parsed < 0)) error = 'Enter 0 or more';
  else if (touched && parsed > max) error = `Cannot exceed ${max}`;
  const effective = error ? null : parsed;
  return {
    unverified,
    received: received === null || Number.isNaN(received) ? null : received,
    max,
    suggested,
    effective,
    toShelf: effective === null || received === null ? null : Math.max(0, received - effective),
    leftUnverified: effective === null ? null : Math.max(0, unverified - effective),
    error,
  };
};

export const isTallyComplete = ({ unverifiedQty, quantityReceived, tallyQty, tallyReason }) => {
  if (!(Number(unverifiedQty) > 0)) return true;
  const state = getTallyState({ unverifiedQty, quantityReceived, tallyQty });
  if (state.error) return false;
  // POM-6111 — a tally of ZERO is a complete answer, not a missing one. It means "this receipt
  // does not explain the claim": the receipt lands in full, the unverified balance stays open for
  // a later correction, and nothing crosses between the buckets. The backend therefore writes no
  // ledger row, so there is nothing for a reason code to explain — and demanding one here would
  // block a legitimate receipt on a lot that happens to carry a claim.
  if (!(state.effective > 0)) return true;
  return String(tallyReason ?? '').trim() !== '';
};

/**
 * POM-6086 - the row this line's lot number is ALREADY in inventory as, or null when the number
 * is new for this drug.
 *
 * Deliberately not pickLotForBud. That one insists on a DATED row because it is choosing an
 * expiration date to adopt, and an undated row has nothing to give it. This asks a different
 * question - has this number been received before, anywhere - and an undated row (a claim raised
 * at a fill window) answers yes to it.
 *
 * Cross-facility, because the lookup is: the same lot legitimately sits at two sites, and that is
 * precisely the case FACILITY_TRANSFER exists to name. Narrowed to the NDC first, since a row
 * under another drug is a different problem with its own message.
 */
export const getExistingLotForNumber = ({ entries, ndc }) =>
  getLotEntriesForNdc({ entries, ndc })[0] ?? null;

/**
 * POM-6086 - whether this line still owes a reason of its own for the repeated lot number.
 *
 * A tally that actually moves quantity already carries a reason for this lot, in this act, from
 * the same vocabulary - and the API takes it as the answer to both questions, so asking again
 * would only invite two different answers on one row.
 *
 * A tally of ZERO does not count. It deliberately carries no reason ("this receipt does not
 * explain the claim"), so the repeat is still unexplained and the picker has to appear - which is
 * also exactly where the API would otherwise refuse a receipt the dialog thought was complete.
 */
export const needsExistingLotReason = ({ existingLot, tallyState }) =>
  Boolean(existingLot) && !(Number(tallyState?.effective) > 0);

export const isExistingLotReasonComplete = ({ existingLot, tallyState, existingLotReason }) =>
  !needsExistingLotReason({ existingLot, tallyState }) ||
  String(existingLotReason ?? '').trim() !== '';

/**
 * The entry whose expiration date a receipt for this lot has to adopt.
 *
 * Prefers the receiving facility's own row - the same lot number can exist at more than one site,
 * and this receipt describes the stock in front of the operator - and otherwise takes any dated
 * row, because an expiration date is a property of the manufacturer's lot, not of a facility.
 */
export const pickLotForBud = ({ entries, receivedAtPharmacyId }) =>
  (entries ?? []).find(
    (entry) => entry?.fulfillingPharmacyId === receivedAtPharmacyId && entry?.expirationDate
  ) ??
  (entries ?? []).find((entry) => entry?.expirationDate) ??
  null;

/**
 * POM-6111 — which BUD wins when a lot lookup finds the lot already exists.
 *
 * The existing lot's date does, always. expiration_date is part of a lot's IDENTITY (it sits
 * inside uq_inv_lot_identity), so a receipt stating a different date does not describe this lot at
 * all — the API refuses it rather than guessing. That makes the looked-up date authoritative and
 * the typed one a guess to be corrected, which is the opposite of the usual "don't clobber what
 * the user typed" instinct.
 *
 * Returns null when no existing dated lot was found, in which case the typed value stands: the
 * lot is new, and its BUD is whatever the packaging says.
 *
 * `replacedFrom` carries the date that was overwritten, and only when it actually differed, so
 * the caller can say so — the field locks once this resolves, and silently swapping a date behind
 * a disabled input is how a receipt gets submitted that nobody described.
 */
export const resolveBudFromLot = ({ entries, receivedAtPharmacyId, typedBud }) => {
  const match = pickLotForBud({ entries, receivedAtPharmacyId });
  if (!match) return null;

  const bud = parse(match.expirationDate, 'yyyy-MM-dd', new Date());
  if (!isValid(bud)) return null;

  const replacedFrom = isValid(typedBud) && !isSameDay(typedBud, bud) ? typedBud : null;
  return { bud, replacedFrom };
};

export const getUnverifiedAtPharmacy = ({ entries, fulfillingPharmacyId }) => {
  if (!fulfillingPharmacyId) return null;
  return (
    (entries ?? []).find(
      (entry) =>
        entry?.fulfillingPharmacyId === fulfillingPharmacyId && Number(entry?.unverifiedQty) > 0
    ) ?? null
  );
};

// A mis-keyed lot number is unrecoverable downstream: nothing validates it against the physical
// stock, so a well-formed typo silently becomes the tracked lot. The entry surfaces pair every lot
// field with a re-key confirmation, and these helpers are the shared rule for that pairing.
//
// The comparison is case-sensitive on purpose — lot formats are case-bearing and a casing slip is
// exactly the kind of typo the re-key exists to catch. Only surrounding whitespace is forgiven.
export const isLotConfirmed = (lot, confirmLot) => {
  const trimmedLot = String(lot ?? '').trim();
  const trimmedConfirm = String(confirmLot ?? '').trim();
  if (!trimmedLot || !trimmedConfirm) return false;
  return trimmedLot === trimmedConfirm;
};

// Same show-when convention as lotFieldError: an untouched confirm field only flags after a save
// attempt, but a mismatch flags the moment there is something to compare.
export const lotConfirmFieldError = (showErrors, lot, confirmLot) => {
  if (!String(confirmLot ?? '').trim()) return showErrors;
  return !isLotConfirmed(lot, confirmLot);
};

export const getLotConfirmHelperText = (showErrors, lot, confirmLot) => {
  if (!String(confirmLot ?? '').trim()) return showErrors ? 'Required' : '';
  return isLotConfirmed(lot, confirmLot) ? '' : 'Lot numbers do not match';
};

// Pass as the confirm field's inputProps — it has to land on the real <input>, not on the MUI
// wrapper. Copy-pasting the first value defeats the whole point of a re-key, so the clipboard and
// drag-drop paths are closed and the browser is told not to autofill it. The first lot field stays
// pasteable on purpose: the verification is the second typing, not the first.
const blockClipboardEvent = (event) => event.preventDefault();
export const NO_PASTE_INPUT_PROPS = {
  autoComplete: 'off',
  onPaste: blockClipboardEvent,
  onDrop: blockClipboardEvent,
};

// Stock is sometimes received against a lot that has already passed its BUD, so the field accepts
// backdated values within this window instead of rejecting every past date outright.
export const BUD_LOOKBACK_DAYS = 60;

// Earliest allowed BUD, inclusive, normalized to start-of-day so the boundary doesn't drift with
// time-of-day. Also feeds the picker's minDate so what's clickable matches what validates.
export const getBudMinDate = () => startOfDay(subDays(new Date(), BUD_LOOKBACK_DAYS));

const isBudProvided = (bud) => bud !== null && bud !== undefined && bud !== '';

const isBudTooOld = (bud) => startOfDay(bud) < getBudMinDate();

export const isBudValid = (bud) => isBudProvided(bud) && isValid(bud) && !isBudTooOld(bud);

// Missing BUD only flags after errors are shown; a typed invalid/too-old date flags immediately.
export const budFieldError = (showErrors, bud) =>
  isBudProvided(bud) ? !isBudValid(bud) : showErrors;

export const getBudHelperText = (showErrors, bud) => {
  if (!isBudProvided(bud)) return showErrors ? 'Required' : '';
  if (!isValid(bud)) return 'Invalid date';
  if (isBudTooOld(bud)) return `BUD cannot be more than ${BUD_LOOKBACK_DAYS} days in the past`;
  return '';
};

// A lot recorded by hand at FILL time is stock going OUT, not stock coming in: it cannot be
// dispensed once it has reached its BUD. So unlike a receipt — which tolerates a short-dated lot
// within BUD_LOOKBACK_DAYS, because stock does arrive that way — the earliest date such a lot can
// carry is tomorrow.
//
// Deliberately string-based, and the only date rule here that is. The fill dialog's field is a
// native `type='date'`, whose value is already a zero-padded `yyyy-MM-dd`, and ISO dates in that
// form sort correctly as plain strings. Parsing them into Date objects to reuse the helpers above
// would reintroduce the UTC-midnight drift that puts "today" a day out either side of the date
// line — on the one rule where being a day out is the whole question.
export const getDispensableBudMinIso = () =>
  format(addDays(startOfDay(new Date()), 1), 'yyyy-MM-dd');

export const isDispensableBud = (bud) => {
  const value = String(bud ?? '').trim();
  return value !== '' && value >= getDispensableBudMinIso();
};

export const getDispensableBudHelperText = (bud) => {
  const value = String(bud ?? '').trim();
  if (!value) return '';
  return isDispensableBud(value) ? '' : 'Must be tomorrow or later';
};

/**
 * POM-6121 — has an EXISTING lot's own BUD passed?
 *
 * Strictly before today, so a lot is dispensable through the end of its expiry date. That is the
 * rule the lot lists already render by (bulkLotNdcStatus's isLotUsable) and the one the server's
 * own isExpired flag carries.
 *
 * Deliberately NOT the tomorrow-or-later floor above. That floor applies to a date the operator
 * TYPES, where a same-day date is almost always a mis-key; an existing lot's date is the
 * manufacturer's, and judging it by the typing floor refuses a lot the same dialog lists as usable
 * and tells the operator it "expired" on a day it has not.
 *
 * `todayStr` is supplied by the caller rather than read from the clock, so the boundary date is
 * testable against a fixed day.
 */
export const isLotBudExpired = ({ expirationDate, todayStr }) => {
  const expiresOn = String(expirationDate ?? '').slice(0, 10);
  const today = String(todayStr ?? '').slice(0, 10);
  if (!expiresOn || !today) return false;
  return expiresOn < today;
};

export const EMPTY_OFFAGE_REASON = { qty: '', reasons: [] };

export const isDirtyOffageEntry = (entry) =>
  String(entry.qty).trim() !== '' || entry.reasons.length > 0;

/** Units shipped but not received. 0 until both quantities are entered. */
export const getOffage = (line) => {
  if (
    String(line.quantityShipped).trim() === '' ||
    String(line.totalQuantityReceived).trim() === ''
  )
    return 0;
  return Math.max(0, Number(line.quantityShipped) - Number(line.totalQuantityReceived));
};

/** Reasons are optional, but once any is entered they must account for the whole offage. */
export const isOffageReasonsValid = (line) => {
  const offage = getOffage(line);
  if (offage === 0) return true;
  const dirtyReasons = (line.offageReasons ?? []).filter(isDirtyOffageEntry);
  if (dirtyReasons.length === 0) return true;
  const sumQty = dirtyReasons.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);
  if (sumQty !== offage) return false;
  return dirtyReasons.every(
    (r) => Number(r.qty) >= 1 && r.reasons.length > 0 && r.reasons.every((t) => t.trim() !== '')
  );
};

/** Opens the reasons section with one empty row the moment an offage appears. */
export const seedOffageReasonRow = (line) =>
  getOffage(line) > 0 && (line.offageReasons ?? []).length === 0
    ? { ...line, offageReasons: [{ ...EMPTY_OFFAGE_REASON }] }
    : line;

/**
 * The reasons in the API's shape. Empty once the offage is gone: a reason typed before the
 * quantities were corrected is hidden with its section, and the API refuses reasons whose total
 * does not equal the offage.
 */
export const getSendableOffageReasons = (line) => {
  if (getOffage(line) === 0) return [];
  return (line.offageReasons ?? [])
    .map((r) => ({ qty: Number(r.qty), reasons: r.reasons.filter((t) => t.trim() !== '') }))
    .filter((r) => r.qty > 0 && r.reasons.length > 0);
};

export const areLineQuantitiesValid = (line) => {
  if (Number(line.quantityOrdered) < 1) return false;
  if (Number(line.quantityShipped) < 1) return false;
  if (Number(line.totalQuantityReceived) < 1) return false;
  if (Number(line.quantityShipped) > Number(line.quantityOrdered)) return false;
  if (Number(line.totalQuantityReceived) > Number(line.quantityShipped)) return false;
  return true;
};

export const getOrderedHelperText = (showErrors, line) => {
  if (showErrors && !String(line.quantityOrdered).trim()) return 'Required';
  if (String(line.quantityOrdered).trim() !== '' && Number(line.quantityOrdered) < 1)
    return 'Must be at least 1';
  return ' ';
};

export const getShippedHelperText = (showErrors, line) => {
  if (showErrors && !String(line.quantityShipped).trim()) return 'Required';
  if (String(line.quantityShipped).trim() !== '' && Number(line.quantityShipped) < 1)
    return 'Must be at least 1';
  if (
    String(line.quantityShipped).trim() !== '' &&
    String(line.quantityOrdered).trim() !== '' &&
    Number(line.quantityShipped) > Number(line.quantityOrdered)
  )
    return 'Cannot exceed Qty Ordered';
  return ' ';
};

export const getReceivedHelperText = (showErrors, line) => {
  if (showErrors && !String(line.totalQuantityReceived).trim()) return 'Required';
  if (String(line.totalQuantityReceived).trim() !== '' && Number(line.totalQuantityReceived) < 1)
    return 'Must be at least 1';
  if (
    String(line.totalQuantityReceived).trim() !== '' &&
    String(line.quantityShipped).trim() !== '' &&
    Number(line.totalQuantityReceived) > Number(line.quantityShipped)
  )
    return 'Cannot exceed Qty Shipped';
  return ' ';
};

export const orderedFieldError = (showErrors, line) =>
  lineFieldError(showErrors, line.quantityOrdered) ||
  (String(line.quantityOrdered).trim() !== '' && Number(line.quantityOrdered) < 1);

export const shippedFieldError = (showErrors, line) =>
  lineFieldError(showErrors, line.quantityShipped) ||
  (String(line.quantityShipped).trim() !== '' && Number(line.quantityShipped) < 1) ||
  (String(line.quantityShipped).trim() !== '' &&
    String(line.quantityOrdered).trim() !== '' &&
    Number(line.quantityShipped) > Number(line.quantityOrdered));

export const receivedFieldError = (showErrors, line) =>
  lineFieldError(showErrors, line.totalQuantityReceived) ||
  (String(line.totalQuantityReceived).trim() !== '' && Number(line.totalQuantityReceived) < 1) ||
  (String(line.totalQuantityReceived).trim() !== '' &&
    String(line.quantityShipped).trim() !== '' &&
    Number(line.totalQuantityReceived) > Number(line.quantityShipped));

export const getNdcHelperText = (showErrors, ndc, isSelected = true) => {
  if (showErrors && !String(ndc).trim()) return 'Required';
  if (showErrors && !isValidNdc(ndc)) return 'Invalid NDC format';
  if (showErrors && isValidNdc(ndc) && !isSelected) return 'Select an option from the list';
  return '';
};
